import { jwtDecode } from 'jwt-decode'
import { defineStore } from 'pinia'

import ApiServiceV1 from '../api/adminApi/AdminApiV1'
import RetailApiV2 from '../api/retailApi/RetailApiV2'
import { actualAppVersion } from '../mixins/useAppRefresher'
import BrowserService from '../services/browserService'
import { GtmEventEmitter } from '@/utils/gtmEventEmitter'
import { clearUserId } from '@/services/userIdentity'
import { useModalStore } from './ModalStore'
import { isAxiosError } from 'axios'

interface ICurrentCustomer {
  id: string,
  firstName: string,
  lastName: string,
  email: string,
  isEmployee: boolean,
  vatPayer: boolean,
  inPlusClub?: boolean,
  plusClubUntil?: string,
  phone?: string,
  phonePrefix?: string,
  dayOfBirth?: number,
  monthOfBirth?: number,
}

interface IApp {
  appVersion: string | null
  currentCustomer: ICurrentCustomer | null
  favoriteProducts: unknown[]
  redirected: boolean
  cookieConsentSettings: string[]
  resetReferrer: boolean
  config: {
    key: string
    value: boolean
  }[]
  checkoutNotice: {
    text: string
    link: string | null
  } | null
}

declare function gtag(...arguments: unknown[]): void;

export const COOKIE_CONSENTS = 'cookie_consents'

export const useAppStore = defineStore('appStore', {
  state: (): IApp => ({
    appVersion: null,
    currentCustomer: null,
    favoriteProducts: [],
    redirected: false,
    cookieConsentSettings: BrowserService.loadCookiesConsentFromStorage(),
    resetReferrer: false,
    config: [],
    checkoutNotice: null,
  }),

  actions: {
    async setAppVersion() {
      this.appVersion = await actualAppVersion()
    },
    setCustomer(customer: ICurrentCustomer, token: string, cart_token: string | undefined): void {
      this.currentCustomer = {
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        isEmployee: customer.isEmployee,
        vatPayer: customer.vatPayer,
        inPlusClub: customer.inPlusClub,
        plusClubUntil: customer.plusClubUntil,
        phone: customer.phone,
        phonePrefix: customer.phonePrefix,
        dayOfBirth: customer.dayOfBirth,
        monthOfBirth: customer.monthOfBirth,
      }

      localStorage.setItem('token', token)
      if (cart_token) {
        localStorage.setItem('cart_token', cart_token)
      } else {
        localStorage.removeItem('cart_token')
      }
    },
    setCookieConsentSettings(consents: string[]) {
      const newConsents = ['technical', ...consents]
      localStorage.setItem(COOKIE_CONSENTS, JSON.stringify(newConsents))
      this.cookieConsentSettings = newConsents
      this.sendCookieConsents()
    },
    async signIn(payload: Record<string, unknown>, oauthLogin: boolean) {
      const { closeModal } = useModalStore()
      let data
      data = oauthLogin
        ? (await ApiServiceV1.post('oauth_sign_in', payload)).data
        : (await ApiServiceV1.post('sign_in', payload)).data
      this.setCustomer(data.customer, data.token, data.cart_token)
      await GtmEventEmitter.login({ id: data.customer.id })
      closeModal()
      this.syncFavoriteProducts()
      return data
    },
    async autoLogin() {
      const token = localStorage.getItem('token')
      if (token) {
        try {
          const decodedToken = jwtDecode<{ customerID: string }>(token)
          const { data } = await ApiServiceV1.put(`sign_in/${decodedToken.customerID}`, {})
          this.setCustomer(data.customer, data.token, data.cart_token)

          this.syncFavoriteProducts()
          return data
        } catch (error) {
          localStorage.removeItem('token')
          // alert(error)
        }
      }
      return
    },
    async signUp(payload: Record<string, unknown>) {
      const { openModal, closeModal } = useModalStore()
      try {
        const { data } = await ApiServiceV1.post('sign_up', payload)
        this.setCustomer(data.customer, data.token, data.cart_token)
        closeModal()
        this.syncFavoriteProducts()
        return data
      } catch (error) {
        localStorage.removeItem('token')
        openModal({
          id: 'modal-alert',
          title: isAxiosError(error) ? error.response?.data.msg : "unknown error",
        })
      }
    },
    signOut() {
      this.currentCustomer = null
      // Resets favoriteProducts to empty array
      this.favoriteProducts.splice(0, this.favoriteProducts.length)
      localStorage.removeItem('token')
      localStorage.removeItem('cart_token')
      clearUserId()
      GtmEventEmitter.logout()
    },
    async deleteProfile() {
      await ApiServiceV1.delete(`customer/profile`)
    },
    async syncFavoriteProducts() {
      const { data } = await ApiServiceV1.get('customer/favorite_products?page=all')
      for (const item of data.favoriteProducts) {
        this.favoriteProducts.push(item.id)
      }
    },
    toggleFavoriteProducts(productId: number | string) {
      if (this.favoriteProducts.includes(productId)) {
        this.removeFromFavoriteProducts(productId)
      } else {
        this.addToFavoriteProducts(productId)
      }
    },
    async addToFavoriteProducts(productId: number | string) {
      const { data } = await ApiServiceV1.post('customer/favorite_products', {
        favorite_products: {
          product_id: productId,
        },
      })
      this.favoriteProducts.push(data.productID)
    },
    async removeFromFavoriteProducts(productId: number | string) {
      const { data } = await ApiServiceV1.delete(`customer/favorite_products/${productId}`)
      this.favoriteProducts = this.favoriteProducts.filter(
        (favoriteProduct) => favoriteProduct !== Number.parseInt(data.productID),
      )
    },
    setResetReferrer(value: boolean) {
      this.resetReferrer = value
    },
    async fetchConfig() {
      // Pass Commission Junction event ID to backend to receive it in Set-Cookie header.
      // This makes the cookie less prone to being blocked by ad blockers.
      const cje = (new URLSearchParams(window.location.search)).get("cjevent")
      const queryParams = cje ? `?cje=${cje}` : ''

      // pass withCredentials so the Set-Cookie header actually sets the cookies
      const { data } = await RetailApiV2.get(`config${queryParams}`, { withCredentials: true })
      this.config = data.config
    },
    async fetchCheckoutNotice() {
      const { data } = await RetailApiV2.get('checkout_notice')
      this.checkoutNotice = data.checkoutNotice
    },
    sendCookieConsents() {
      // When cookie consent is changed additionally.
      if (this.cookieConsentSettings.length) {
        gtag('consent', 'update', {
          ad_storage: this.isAdvertisementConsentAllow ? 'granted' : 'denied',
          analytics_storage: this.isAnalyticConsentAllow ? 'granted' : 'denied',
          ad_user_data: this.isAdvertisementConsentAllow ? 'granted' : 'denied',
          ad_personalization: this.isAdvertisementConsentAllow ? 'granted' : 'denied',
          functionality_storage: 'granted',
          personalization_storage: this.isPersonalConsentAllow ? 'granted' : 'denied',
          security_storage: 'granted',
        })
        // @ts-expect-error global variable hack
        window.dataLayer?.push({
          event: 'consent_update',
          consent: {
            ad_storage: this.isAdvertisementConsentAllow ? 'granted' : 'denied',
            analytics_storage: this.isAnalyticConsentAllow ? 'granted' : 'denied',
            ad_user_data: this.isAdvertisementConsentAllow ? 'granted' : 'denied',
            ad_personalization: this.isAdvertisementConsentAllow ? 'granted' : 'denied',
            functionality_storage: 'granted',
            personalization_storage: this.isPersonalConsentAllow ? 'granted' : 'denied',
            security_storage: 'granted',
          },
          _clear: true,
        })
      }
    }
  },

  getters: {
    getCurrentCustomer: (state) => state.currentCustomer,
    getFavoriteProducts: (state) => state.favoriteProducts,
    isSignedIn: (state) => state.currentCustomer !== null,
    isFavorite: (state) => (productId: number | string) => state.favoriteProducts.includes(productId),
    isConsentsSet: (state) => state.cookieConsentSettings.length,
    getConsents: (state) => state.cookieConsentSettings,
    getResetReferrer: (state) => state.resetReferrer,
    getAppVersion: (state) => state.appVersion,
    isMobileAppNotice: (state) => state.config.find((item) => item.key === 'mobileapp_notice')?.value,
    isFreeTransportNotice: (state) => state.config.find((item) => item.key === 'freetransport_notice')?.value,
    isAnalyticConsentAllow: (state) => state.cookieConsentSettings.includes('analytic'),
    isPersonalConsentAllow: (state) => state.cookieConsentSettings.includes('personal'),
    isAdvertisementConsentAllow: (state) => state.cookieConsentSettings.includes('advertisement'),
  },
})
